import * as React from "react";
import { Radio, RadioGroup as AriakitRadioGroup, useRadioStore } from "@ariakit/react";
import { cn } from "@/lib/utils";

const RadioGroupContext = React.createContext<{ required?: boolean }>({});

interface RadioGroupProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, value, defaultValue, onValueChange, required, children, ...props }, ref) => {
    const store = useRadioStore({ 
      value, 
      defaultValue,
      setValue: onValueChange ? (value: string | number | null) => onValueChange(value as string) : undefined,
    });

    return (
      <RadioGroupContext.Provider value={{ required }}>
        <AriakitRadioGroup
          ref={ref}
          store={store}
          className={cn("grid gap-2", className)}
          {...props}
        >
          {children}
        </AriakitRadioGroup>
      </RadioGroupContext.Provider>
    );
  }
);
RadioGroup.displayName = "RadioGroup";

interface RadioGroupItemProps {
  value: string;
  id?: string;
  className?: string;
  disabled?: boolean;
}

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ className, value, id, disabled, ...props }, ref) => {
    const { required } = React.useContext(RadioGroupContext);
    return (
      <Radio
        ref={ref}
        value={value}
        id={id}
        disabled={disabled}
        required={required}
        className={cn(
          "aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[checked]:bg-primary data-[checked]:border-primary",
          "relative after:absolute after:inset-0 after:m-auto after:h-2 after:w-2 after:rounded-full",
          "data-[checked]:after:bg-primary-foreground",
          className
        )}
        {...props}
      />
    );
  }
);
RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
